import React from 'react';
import { Globe, FileText, Briefcase, Calendar, Tag, Image, Video, ShoppingBag, MessageCircle, ArrowRight, CheckCircle2, Megaphone } from 'lucide-react';
import { Button } from '../ui/button';
import { usePodPanel } from '../../pages/LandingPage';

const BestOfBestDirectory = () => {
  const { openPodPanel } = usePodPanel();

  const publishTypes = [
    { icon: FileText, label: 'Articles' },
    { icon: Briefcase, label: 'Jobs' },
    { icon: Calendar, label: 'Events' },
    { icon: Tag, label: 'Coupons' },
    { icon: Image, label: 'Photos' },
    { icon: Video, label: 'Videos' },
    { icon: ShoppingBag, label: 'Products' },
    { icon: MessageCircle, label: 'Discuss' },
  ];

  const channels = [
    'Meta (Facebook & Instagram)',
    'Google Ads',
    'TikTok',
    'TV & Radio',
    'Email Campaigns',
    'Direct Mail & Postcards',
  ];

  const directoryBenefits = [
    'SEO-optimized listing for Google & AI search engines',
    'Your own AI Directory Hero for 24/7 lead capture',
    'Multi-channel retargeting pixels built-in',
    'High-quality backlink to boost domain authority',
    'Only 1 business per category per 100K population',
  ];

  const retargetingChannels = [
    { name: 'Meta', desc: 'Facebook, Instagram, Reels, Stories' },
    { name: 'Google', desc: 'Search, Display, YouTube' },
    { name: 'TV', desc: '500+ Premium Stations' },
    { name: 'Radio', desc: '1000+ Radio Stations' },
    { name: 'Direct Mail', desc: 'Postcards with QR tracking' },
    { name: 'Email', desc: 'Nurture campaigns' },
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-violet-500/20 border border-violet-500/30 rounded-full px-5 py-2 mb-6">
            <Globe className="w-5 h-5 text-violet-400" />
            <span className="text-white font-semibold">Community Platform</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            One Listing. <span className="text-cyan-400">More Visibility.</span> <span className="text-emerald-400">Everywhere.</span>
          </h2>
          
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            When you list in the Best of Best Directory, you don't just get listed — you get a local-search-optimized profile with your own AI Directory Hero, built to capture leads 24/7 and retarget across all platforms.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 mb-16">
          {/* Business Directory */}
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Globe className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Business Directory</h3>
                <p className="text-slate-400 text-sm">Get Found by Local Customers</p>
              </div>
            </div>

            <p className="text-slate-300 mb-6">
              List once, get seen everywhere. Your listing includes built-in retargeting pixels for Meta, Google, TV, Radio, and Direct Mail — so one visit triggers ongoing visibility across platforms.
            </p>

            <ul className="space-y-3 mb-6">
              {directoryBenefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-sm">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* Best of Best Badge Preview */}
            <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png"
                alt="Best of Best Badge"
                className="w-16 h-16 rounded-lg"
              />
              <div>
                <p className="text-white font-semibold">Become "Best of Best" Certified</p>
                <p className="text-slate-400 text-sm">Stand out with our trusted AI certification badge</p>
              </div>
            </div>
          </div>

          {/* Publishing Hub */}
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Publishing Hub</h3>
                <p className="text-slate-400 text-sm">Publish Everywhere, All at Once</p>
              </div>
            </div>

            <p className="text-slate-300 mb-6">
              Publish articles, events, job listings, coupons, and more across multiple platforms simultaneously. Build your brand presence everywhere.
            </p>

            {/* Publish Types */}
            <div className="grid grid-cols-4 gap-2 mb-6">
              {publishTypes.map((type, idx) => {
                const Icon = type.icon;
                return (
                  <div key={idx} className="bg-slate-900/60 rounded-lg p-3 text-center hover:bg-slate-900 transition-colors">
                    <Icon className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                    <span className="text-slate-400 text-xs">{type.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Channels */}
            <div className="bg-slate-900/60 rounded-xl p-4">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Distributes To:</p>
              <div className="flex flex-wrap gap-2">
                {channels.map((channel, idx) => (
                  <span key={idx} className="bg-slate-800 text-slate-300 text-xs px-3 py-1.5 rounded-full border border-slate-700">
                    {channel}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* How It Connects */}
        <div className="bg-gradient-to-r from-slate-800/80 to-slate-800/40 border border-slate-700 rounded-2xl p-8 mb-12">
          <h3 className="text-xl font-bold text-white text-center mb-8">
            How Directory + Publishing + POD Connect
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-bold text-xl mx-auto mb-4">1</div>
              <p className="text-white font-semibold mb-2">List Your Business</p>
              <p className="text-slate-400 text-sm">Get discovered in the <span className="text-cyan-400">Directory</span></p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400 font-bold text-xl mx-auto mb-4">2</div>
              <p className="text-white font-semibold mb-2">Publish Content</p>
              <p className="text-slate-400 text-sm">Engage customers & build authority with <span className="text-violet-400">Publishing</span></p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-xl mx-auto mb-4">3</div>
              <p className="text-white font-semibold mb-2">Scale with POD</p>
              <p className="text-slate-400 text-sm">Use <span className="text-emerald-400">POD services</span> to grow your business</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-lg text-slate-400 mb-6">
            All included with your AI Horse-Powered system — <span className="text-emerald-400 font-semibold">no extra cost</span>.
          </p>
          <Button
            onClick={openPodPanel}
            size="lg"
            className="bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 text-white font-bold px-10 py-6 text-lg rounded-xl transition-all hover:shadow-xl hover:shadow-violet-500/30 group"
          >
            Explore the Platform
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BestOfBestDirectory;
